import { useTranslation } from 'react-i18next';
import {
    ArrowRightLeft, Download, Eye, Heart, Image as ImageIcon, Mail, MailOpen, MessageCircle, MessagesSquare, MousePointerClick, Inbox, Calculator,
    Share2, ShoppingCart, Sparkles, StickyNote, type LucideIcon,
} from 'lucide-react';

import { RelativeTime } from '../../../../components/design-system/admin';
import type { TimelineEntry } from '../../../../common/hooks/use-lead-desk-api.hook';

const ICON: Record<string, LucideIcon> = {
    visit: Eye,
    tried: Sparkles,
    download: Download,
    room_upload: ImageIcon,
    estimate: Calculator,
    quote: Calculator,
    cart: ShoppingCart,
    share: Share2,
    render: ImageIcon,
    chat: MessagesSquare,
    product_page: MousePointerClick,
    favorite: Heart,
    email_sent: Mail,
    email_opened: MailOpen,
    email_clicked: MousePointerClick,
    request: Inbox,
    contacted: MessageCircle,
    stage: ArrowRightLeft,
    note: StickyNote,
};

/** The lead's story, newest first — runs of the same thing on one day folded into one line. */
export function LeadTimeline({ entries, productTitle }: { entries: TimelineEntry[]; productTitle: (id: string) => string | null }) {
    const { t } = useTranslation();
    if (!entries.length) return <p className="text-sm text-muted-foreground">{t('LEAD_DESK.FILE.NO_TIMELINE')}</p>;
    return (
        <ol className="flex flex-col" data-testid="lead-timeline">
            {entries.map((e, i) => {
                const Icon = ICON[e.kind] ?? Eye;
                const detail = e.kind === 'request'
                    ? t(`LEAD_DESK.REQUEST.${String(e.detail).toUpperCase()}`, { defaultValue: e.detail })
                    : e.kind === 'stage'
                        ? t(`LEAD_DESK.STAGE.${String(e.detail).toUpperCase()}`, { defaultValue: e.detail })
                        : e.kind === 'contacted'
                            ? t(`LEAD_DESK.COMPOSER.CHANNEL.${String(e.detail).toUpperCase()}`, { defaultValue: e.detail })
                            : e.detail;
                const products = (e.productIds ?? []).map(productTitle).filter(Boolean).slice(0, 3);
                return (
                    <li key={`${e.at}-${i}`} className="flex gap-3 border-b py-2.5 last:border-b-0">
                        <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md border text-muted-foreground">
                            <Icon className="size-3.5" aria-hidden />
                        </span>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm">
                                {t(`LEAD_DESK.TIMELINE.${e.kind.toUpperCase()}`, { defaultValue: e.kind })}
                                {detail ? <span className="text-muted-foreground"> · {detail}</span> : null}
                                {e.count > 1 && <span className="ms-1.5 text-xs tabular-nums text-muted-foreground">{t('LEAD_DESK.TIMELINE.TIMES', { n: e.count })}</span>}
                            </p>
                            {products.length > 0 && <p className="truncate text-xs text-muted-foreground">{products.join(', ')}</p>}
                        </div>
                        <RelativeTime value={e.at} className="shrink-0 text-xs text-muted-foreground" />
                    </li>
                );
            })}
        </ol>
    );
}
